import * as React from 'react';
import PropTypes from 'prop-types';
import Dialog from '../../dialogs/Dialog';
import Button from '../../buttons/Button';
import DropDownList from '../../forms/DropDownList';
import SaveFilter from './SaveFilterDialog';
import ConfirmDialog from '../../dialogs/ConfirmDialog';
import './DialogFilter.scss';
import { buildUrl, addTextField, getHistoryFilter, removeFormFields, isDateType } from '../../utils/helper';
import Checkbox from '../../forms/Checkbox';
import sk from '../../res/sk.json';
import Input from '../../forms/Input';
import DateInput from '../../forms/DateInput';
import NumericTextBox from '../../forms/NumericTextBox';
import ComboBox from '../../forms/ComboBox';
import DateRangePicker from '../../forms/DateRangePicker';
import _ from 'lodash';

const NUMERIC_OPS = [
  { text: sk.grid.filterEqOperator, operator: 'eq' },
  { text: sk.grid.filterNotEqOperator, operator: 'neq' },
  { text: sk.grid.filterGteOperator, operator: 'gte' },
  { text: sk.grid.filterGtOperator, operator: 'gt' },
  { text: sk.grid.filterLteOperator, operator: 'lte' },
  { text: sk.grid.filterLtOperator, operator: 'lt' },
  { text: sk.grid.filterIsNullOperator, operator: 'isnull' },
  { text: sk.grid.filterIsNotNullOperator, operator: 'isnotnull' }
];

const TEXT_OPS = [
  { text: sk.grid.filterContainsOperator, operator: 'contains' },
  { text: sk.grid.filterNotContainsOperator, operator: 'doesnotcontain' },
  { text: sk.grid.filterEqOperator, operator: 'eq' },
  { text: sk.grid.filterNotEqOperator, operator: 'neq' },
  { text: sk.grid.filterStartsWithOperator, operator: 'startswith' },
  { text: sk.grid.filterEndsWithOperator, operator: 'endswith' },
  { text: sk.grid.filterIsEmptyOperator, operator: 'isempty' },
  { text: sk.grid.filterIsNotEmptyOperator, operator: 'isnotempty' }
];

const DATE_OPS = [
  { text: sk.grid.filterEqOperator, operator: 'eq' },
  { text: sk.grid.filterNotEqOperator, operator: 'neq' },
  { text: sk.grid.filterAfterOrEqualOperator, operator: 'gte' },
  { text: sk.grid.filterAfterOperator, operator: 'gt' },
  { text: sk.grid.filterBeforeOperator, operator: 'lt' },
  { text: sk.grid.filterBeforeOrEqualOperator, operator: 'lte' },
  { text: sk.grid.filterIsNullOperator, operator: 'isnull' },
  { text: sk.grid.filterIsNotNullOperator, operator: 'isnotnull' },
  { text: sk.grid.filterBetweenOperator, operator: 'between' },
];

const FILTER_OPERATORS = {
  text: TEXT_OPS,
  string: TEXT_OPS,
  numeric: NUMERIC_OPS,
  unitnumeric: NUMERIC_OPS,
  date: DATE_OPS,
  dateinput: DATE_OPS,
  datetime: DATE_OPS,
  datetimeinput: DATE_OPS,
  container: TEXT_OPS,
  boolean: [
    { text: sk.grid.filterEqOperator, operator: 'eq' },
    { text: sk.grid.filterNotEqOperator, operator: 'neq' }
  ],
  enum: [
    { text: sk.grid.filterEqOperator, operator: 'eq' },
    { text: sk.grid.filterNotEqOperator, operator: 'neq' }
  ],
  buttongroup: [
    { text: sk.grid.filterEqOperator, operator: 'eq' },
    { text: sk.grid.filterNotEqOperator, operator: 'neq' }
  ],
  radio: [
    { text: sk.grid.filterEqOperator, operator: 'eq' },
    { text: sk.grid.filterNotEqOperator, operator: 'neq' }
  ],
  cmd: [
    { text: sk.grid.filterContainsOperator, operator: 'contains' },
    { text: sk.grid.filterNotContainsOperator, operator: 'doesnotcontain' },
    { text: sk.grid.filterEqOperator, operator: 'eq' },
    { text: sk.grid.filterNotEqOperator, operator: 'neq' },
  ]
};



const _boolean = [
  { text: sk.grid.filterBooleanAll, operator: null },
  { text: sk.grid.filterIsTrue, operator: true },
  { text: sk.grid.filterIsFalse, operator: false }
];
const _join = [{ value: false, text: "súčasne" }, { value: true, text: "alebo" }];
const _left = [" - ", "(", "((", "(((", "((((", "(((((", "(((((("];
const _right = [" - ", ")", "))", ")))", "))))", ")))))", "))))))"];

/**
 * Filter in dialog
 * @module
 * @param {array} schema - Schema
 * @param {object} [parentProps] - Parent props
 * @param {bool} isOpen - Is dialog open?
 * @param {func} handleOpenFilter - Function to open filter
 * @param {func} handleCloseFilter - Functions to close filter
 * @param {object} filter - Filter
 * @param {string} enumDefault - Default value in enum
 * @param {sting} primaryField - Primary field
 * @param {bool} userFilter - User filter
 * @param {bool} userFilterDisabled - User filter is disabled
 * @param {array} [userFilterList] - List of user filters
 * @param {func} getSchema - Get schema
 * @param {func} getDefaultUserId - Get default user ID
 * @param {string} [db] - db
 */
class DialogFilterExtended extends React.Component {
  alertTimeout;

  constructor(props) {
    super(props);
    this.state = {
      isOpenSaveDialog: false,
      isOpenDelete: false,
      selectFilter: null,
      filter: props.filter.filters,
      startFilter: false,
      alertMsg: null,
      error: null,
      nameError: null,
      filterId: 0,
      history: 0,
      user: null,
    }
  }

  buildFilterExtended = (input) => {
    let newFilters = []
    for (let x of input) {
      if (x.item && x.item.type === 'date' && x.value && x.value instanceof Date) {
        x.value.setHours(0, 0, 0, 0);
      }

      const field = x.item ? (x.item.filter === "container" ? x.item.field + "." + x.item.textField : x.item.field) : null;
      let newFilterItem = {
        field: field,
        operator: x.operator,
        value: x.value !== undefined && x.value !== null ? x.value : null,
        join: x.join || false,
        left: x.left && x.left.includes("(") ? x.left : null,
        right: x.right && x.right.includes(")") ? x.right : null,
        order: x.id,
        frmPod: x.form?.IFrm || null,
        parentField: x.form?.ParentField || null,
        childField: x.form?.ChildField || null,
        filterId: x.filterId,
        filterType: x.filterType
      }
      newFilters = [...newFilters, newFilterItem]
    }
    const newFilter = {
      filters: [
        ...newFilters,
      ]
    }
    return newFilter;
  }

  getOperator = (operator, filter) => {
    if (_.isObject(operator)) return operator;

    switch (operator) {
      case 'custom':
        return { text: 'definovaný filter', operator: 'custom' }
      case 'exists':
        return { text: 'existuje záznam', operator: 'exists' }
      case 'notexists':
        return { text: 'neexistuje záznam', operator: 'notexists' }
      default:
        return FILTER_OPERATORS[filter && filter !== "none" ? filter : 'text'].find(el => el.operator === operator);
    }
  }

  prepareDateValue = (x, item) => {
    // date value from string
    let value = x.value;
    if (item && isDateType(item.type) && x.operator !== "between" && typeof value === 'string') {
      value = new Date(value)
    }
    if (item && isDateType(item.type) && x.operator === "between" && typeof value === "object" && value.start && typeof value.start === 'string') {
      value = { start: new Date(value.start), end: new Date(value.end) }
    }
    return value
  }

  prepareNewFilters = async (filters) => {
    if (filters.length === 0) return []
    let newFilters = [];

    await Promise.all(filters.map(async (x) => {
      let form = this.props.parentProps.frms.find(el => el.IFrm === x.frmPod) || null;
      let schema = this.props.schema;

      if (form) {
        let getSchema = await this.props.getSchema(form.Identif, this.props.db)
        schema = getSchema.Grid
      }
      let item = schema.find(el => el.field === x.field || (el.field + "." + el.textField) === x.field);
      let id = x.order;

      const value = this.prepareDateValue(x, item);
      const join = x.join || false;
      const left = x.left && x.left.includes("(") ? x.left : null;
      const right = x.right && x.right.includes(")") ? x.right : null;

      newFilters = [...newFilters, { ...x, id, item, form, schema, value, join, left, right }]
      return x;
    }));

    return newFilters;
  }

  prepareFilter = async (filters) => {
    const newFilters = await this.prepareNewFilters(filters);

    let filterId = newFilters.length > 0 ? Math.max(...newFilters.map(el => el.id)) + 1 : 0;
    this.setState({
      filter: newFilters,
      filterId
    })
  }

  compareUserFilter = (filters) => {
    const comparisonFilter = removeFormFields(filters);
    if (filters.length > 0) {
      for (let userFilter of this.props.userFilterList) {
        const comparisonUserFilter = removeFormFields(userFilter.filters);
        if (_.xorWith(comparisonFilter, comparisonUserFilter, _.isEqual).length === 0) {
          return userFilter;
        }
      }
    }
    return null;
  }

  prepareUserFilter = (filters) => {
    const selectFilter = this.compareUserFilter(filters);
    if (selectFilter) {
      this.handleChangeUserFilter("selectFilter", selectFilter)
    }
    else {
      this.setState({
        selectFilter: null,
        startFilter: false,
      })
    }
  }

  setDefaultUser = async () => {
    const iUz = await this.props.getDefaultUserId();
    if (iUz !== this.state.user) {
      this.setState({
        user: iUz,
        selectFilter: null,
        startFilter: false
      });
      await this.props.getFilterList(iUz);
    }
  }

  componentDidMount = async () => {
    await this.setDefaultUser()
    await this.prepareFilter(this.state.filter);
    this.prepareUserFilter(this.state.filter);

    if (this.state.filter?.length === 0) {
      this.addNewFilter();
    }
  }

  static getDerivedStateFromProps(props, state) {
    return { favoritefood: props.favfod };
  }

  setAlert = (msg, isError) => {
    this.setState({
      alertMsg: msg,
      error: isError
    })
    clearTimeout(this.alertTimeout)
    this.alertTimeout = setTimeout(() => {
      this.setState({
        error: null,
        alertMsg: null
      })
    }, 4500)
  }

  handleDeleteFilter = () => {
    if (this.state.selectFilter) {
      try {
        this.props.deleteFilter(this.state.selectFilter.id, this.state.user);
        this.setState({
          filter: [],
          selectFilter: null,
          startFilter: false
        })
        this.setAlert("Filter úspešne vymazaný", false);
      }
      catch (error) {
        this.setAlert("Nepodarilo sa vymazať filter", true);
      }
    }
  }

  handleChangeUserFilter = (name, value) => {
    const filter = { filters: addTextField(value.filters, this.props.schema) };
    this.prepareFilter(filter.filters)

    this.setState({
      selectFilter: value,
      startFilter: value.disabled === undefined ? false : !value.disabled
    });
  }

  setHistoryFilter = async (history) => {
    await this.setDefaultUser();
    let allFilters = getHistoryFilter(this.props.primaryField);
    let filters = allFilters?.[history] || [];
    this.prepareFilter(filters);
    this.prepareUserFilter(filters);
    this.setState({ history });
  }

  next = () => {
    const history = this.state.history - 1
    this.setHistoryFilter(history);
  }

  disabledNext = () => {
    return this.state.history === 0;
  }

  previous = async () => {
    let history = this.state.history;
    if (history === 0) {
      const oldFilter = getHistoryFilter(this.props.primaryField);
      const oldFilters = await this.prepareNewFilters(oldFilter[0])
      const value = this.buildFilterExtended(this.state.filter);
      if (_.xorWith(removeFormFields(value.filters), removeFormFields(oldFilters), _.isEqual).length !== 0) {
        history = 0;
      }
      else {
        history = 1;
      }
    }
    else {
      history++;
    }
    this.setHistoryFilter(history);
  }

  disabledPrevious = () => {
    const historyFilter = getHistoryFilter(this.props.primaryField);
    return historyFilter.length <= this.state.history + 1 || historyFilter.length === 0
  }

  handleSaveFilter = async (name) => {
    if (this.props.userFilterList.find(el => el.name === name)) {
      this.setState({ nameError: "Filter s týmto menom už existuje." })
      return;
    }
    try {
      const input = this.state.filter;
      const newFilter = this.buildFilterExtended(input);
      const filter = { name, filters: newFilter.filters, disabled: true };
      let newFilterList = await this.props.createFilter(filter, this.state.user);
      let selectFilter = newFilterList.find(el => el.name === name);
      this.handleChangeUserFilter("selectFilter", selectFilter);
      this.handleCloseSaveDialog();
      this.setAlert("Filter úspešne vytvorený", false);
    }
    catch (error) {
      this.setAlert("Nepodarilo sa uložiť filter", true);
    }
  }

  handleUpdateFilter = async () => {
    try {
      this.props.deleteFilter(this.state.selectFilter.id, this.state.user);
      const name = this.state.selectFilter.name;
      const input = this.state.filter
      const newFilter = this.buildFilterExtended(input);
      const filter = { name, filters: newFilter.filters, disabled: !this.state.startFilter };
      let newFilterList = await this.props.createFilter(filter, this.state.user);
      let selectFilter = newFilterList.find(el => el.name === name);
      this.handleChangeUserFilter("selectFilter", selectFilter);
      this.setAlert("Filter úspešne upravený", false);
    }
    catch (error) {
      this.setAlert("Nepodarilo sa uložiť filter", true);
    }
  }

  handleOpenSaveDialog = () => {
    this.setState({ isOpenSaveDialog: true });
  }

  handleCloseSaveDialog = () => {
    this.setState({ isOpenSaveDialog: false, nameError: null });
  }

  handleConfirmFilter = () => {
    const input = this.state.filter;
    let newFilter = this.buildFilterExtended(input);
    if (this.state.selectFilter && this.compareUserFilter(newFilter.filters)) {
      newFilter = this.state.selectFilter;
    }
    this.props.handleConfirmFilter(newFilter);
  }

  handleOpenDelete = () => {
    this.setState({ isOpenDelete: true });
  }

  handleCloseDelete = () => {
    this.setState({ isOpenDelete: false });
  }

  handleConfirmDelete = () => {
    this.handleDeleteFilter();
    this.handleCloseDelete();
  }

  handleStartFilter = (name, value) => {
    try {
      this.setState({ startFilter: value })
      this.props.updateFilter(this.state.selectFilter.id, !value, this.state.selectFilter.type, this.state.user);
      const msg = value ? "Štartovací filter bol zapnutý" : "Štartovací filter bol vypnutý";
      this.setAlert(msg, false);
    }
    catch (error) {
      this.setAlert("Nepodarilo sa nastaviť štartovací filter", true);
    }
  }

  addNewFilter = () => {
    this.setState(state => ({ filter: [...state.filter, { id: state.filterId }], filterId: state.filterId + 1 }));
  }

  removeFilter = (item) => {
    const filter = this.state.filter.filter(el => el.id !== item.id);
    this.setState({ filter });
  }


  handleChange = async (name, value, id) => {
    let filter = await Promise.all(this.state.filter.map(async (item) => {
      if (item.id === id) {
        item[name] = ["left", "right"].includes(name) ? value.text : name === "operator" ? value.operator : value;

        if (name === "form") {
          if (value.Identif) {
            const schema = await this.props.getSchema(value.Identif, this.props.db)
            item.schema = schema.Grid;
          }
          else {
            item.schema = this.props.schema;
          }

          item.item = null;
          item.operator = null;
          item.value = null;
        }
        else if (name === "item") {
          item.operator = FILTER_OPERATORS[value.filter && value.filter !== "none" && value.filter !== "string" ? value.filter : 'text'][0]?.operator;
          item.value = null;
        }
        else if (name === "operator" && ['isnull', 'isnotnull'].includes(value.operator)) {
          item.value = null;
        }
      }
      return item;
    }));
    this.setState({ filter });
  }

  handleChangeUser = async (name, value) => {
    if (value) {
      this.setState({
        [name]: value.IUz,
        selectFilter: null,
        startFilter: false
      });
      this.props.getFilterList(value.IUz);
    }
    else {
      this.setState({
        [name]: null,
        selectFilter: null,
        startFilter: false
      });
    }
  }

  onBlurUser = () => {
    if (!this.state.user) {
      this.setDefaultUser();
    }
  }

  orderSchema = (schema) => {
    return _.sortBy(schema.filter(x => x.Filterable), "OrderFilter");
  }

  hasValue = (value) => {
    return value && value !== this.props.enumDefault;
  }


  getValueInput = (dataItem, type) => {
    const operator = dataItem?.operator;
    const customType = operator === "custom" ? "container" : type;
    switch (customType) {
      case "enum":
      case "buttongroup":
      case "radio":
        const primaryField = dataItem.item.primaryField;
        const data = this.props.parentProps[dataItem.item.dataField] || dataItem.item.EnumData;
        const enumValue = primaryField ? data.find(el => el[primaryField] === Number(this.state.filter.find(x => x.id === dataItem.id)?.value)) : this.state.filter.find(x => x.id === dataItem.id)?.value;

        return (
          <div className="filter-input value">
            <DropDownList
              name={"value"}
              data={data}
              onChange={(name, value) => {
                const hasValue = this.hasValue(value);
                this.handleChange(name, hasValue ? (primaryField ? value[primaryField] : value) : '', dataItem.id)
              }}
              value={enumValue || this.props.enumDefault}
              textField={dataItem.item.textField}
              defaultItem={this.props.enumDefault}
              disabled={['custom', 'isnull', 'isnotnull'].includes(operator)}
            />
          </div>
        )
      case "boolean":
        return (
          <div className="filter-input value">
            <DropDownList
              name={"value"}
              data={_boolean}
              onChange={(name, value) => this.handleChange(name, value.operator, dataItem.id)}
              value={_boolean.find(el => el.operator === dataItem.value)}
              textField="text"
              disabled={['custom', 'isnull', 'isnotnull'].includes(operator)}
            />
          </div>
        )
      case "date":
      case 'datetime':
      case "dateinput":
      case 'datetimeinput':
        return (
          <div className="filter-input value">
            {operator === 'between' &&
              <DateRangePicker
                name={"value"}
                onChange={(name, value) => this.handleChange(name, value, dataItem.id)}
                value={dataItem.value}
                calendarSettings={{
                  bottomView: 'month',
                  topView: 'year',
                  views: 3
                }}
                format={{
                  year: "numeric",
                  month: "short",
                  day: "numeric"
                }}
                disabled={['custom', 'isnull', 'isnotnull'].includes(operator)}
              />}
            {operator !== 'between' &&
              <DateInput
                name={"value"}
                onChange={(name, value) => this.handleChange(name, value, dataItem.id)}
                value={dataItem.value instanceof Date ? dataItem.value : null}
                disabled={['custom', 'isnull', 'isnotnull'].includes(operator)}
                type={type.replace("input", "")}
                validationMessage={" "}
              />}
          </div>
        )
      case "numeric":
      case "unitnumeric":
        return (
          <div className="filter-input value">
            <NumericTextBox
              name={'value'}
              onChange={(name, value) => this.handleChange(name, value, dataItem.id)}
              value={dataItem.value}
              disabled={['custom', 'isnull', 'isnotnull'].includes(operator)}
            />
          </div>
        )
      case "container":
      default:
        return (
          <div className="filter-input value">
            <Input
              name={'value'}
              onChange={(name, value) => this.handleChange(name, value, dataItem.id)}
              value={_.toString(dataItem.value)}
              disabled={['custom', 'isnull', 'isnotnull'].includes(operator)}
            />
          </div>
        )
    }
  }

  buildFilterRow = () => {
    return (
      <React.Fragment>
        {this.state.filter.map((dataItem, index) => {
          let operators = FILTER_OPERATORS[dataItem.item?.type] || FILTER_OPERATORS.text;
          if (dataItem.form) {
            operators = [...operators,
            { text: 'existuje záznam', operator: 'exists' },
            { text: 'neexistuje záznam', operator: 'notexists' }];
          }

          const operator = dataItem?.operator;
          const frms = this.props.parentProps?.frms.filter(x => x.ParentField && x.ChildField);
          return <tr key={index}>
            <td>
              <div className="k-form-field filter-input">
                <DropDownList
                  name={"join"}
                  data={_join}
                  onChange={(name, value) => this.handleChange(name, value.value, dataItem.id)}
                  value={_join.find(el => el.value === dataItem.join) || _join[0]}
                  textField="text"
                />
              </div>
            </td>
            <td className={"left"}>
              <div className="k-form-field filter-input">
                <DropDownList
                  name={"left"}
                  data={_left.map(x => ({ text: x }))}
                  onChange={(name, value) => this.handleChange(name, value, dataItem.id)}
                  value={{ text: dataItem.left || "" }}
                  textField="text"
                />
              </div>
            </td>
            <td>
              <div className="k-form-field filter-input">
                <DropDownList
                  name="form"
                  data={frms}
                  onChange={(name, value) => this.handleChange(name, value, dataItem.id)}
                  value={dataItem.form}
                  textField="Name"
                  defaultItem={{ Name: ' - ' }}
                />
              </div>
            </td>
            <td>
              <div className="k-form-field filter-input">
                <DropDownList
                  name="item"
                  data={this.orderSchema(dataItem.schema || this.props.schema)}
                  onChange={(name, value) => this.handleChange(name, value, dataItem.id)}
                  value={dataItem.item}
                  textField="FilterTitle"
                  disabled={operator === "custom"}
                />
              </div>
            </td>
            <td>
              <div className="k-form-field filter-input">
                <DropDownList
                  name={"operator"}
                  data={operators}
                  onChange={(name, value) => this.handleChange(name, value, dataItem.id)}
                  value={this.getOperator(operator, dataItem.item?.type)}
                  textField="text"
                  disabled={operator === "custom"}
                />
              </div>
            </td>
            <td className="filter-value">
              <div className="k-form-field filter-input">
                {this.getValueInput(dataItem, dataItem.item?.type)}
              </div>
            </td>
            <td className={"right"}>
              <div className="k-form-field filter-input">
                <DropDownList
                  name={"right"}
                  data={_right.map(x => ({ text: x }))}
                  onChange={(name, value) => this.handleChange(name, value, dataItem.id)}
                  value={{ text: dataItem.right || "" }}
                  textField="text"
                />
              </div>
            </td>
            <td className={"dialog-filter-close"}>
              <div className="k-form-field filter-input">
                <Button
                  onClick={() => this.removeFilter(dataItem)}
                  icon="close"
                />
              </div>
            </td>
          </tr>
        })}
      </React.Fragment>
    );
  }

  render() {
    const editableFilter = this.props.userFilterDisabled || !this.state.selectFilter?.id || this.state.selectFilter?.type !== 3;
    const startableFilter = this.props.userFilterDisabled || !this.state.selectFilter?.id || !this.state.selectFilter?.CanDisabled;

    return (
      <>
        <Dialog
          isOpen={this.props.isOpen}
          onClose={this.props.handleCloseFilter}
          onConfirm={this.handleConfirmFilter}
          title="Filter"
          width={1260}
          disableConfirmOnEnter
        >
          <div>
            {this.props.userFilter &&
              <div className="toolbar filter-toolbar">
                <div className="left">
                  <Button
                    disabled={this.props.userFilterDisabled}
                    onClick={this.handleOpenSaveDialog}
                    title="Nový filter"
                    imageUrl={buildUrl('assets/icons/toolbar_cg_new.svg')}
                  />
                  <Button
                    disabled={editableFilter}
                    onClick={this.handleUpdateFilter}
                    title="Uložiť filter"
                    imageUrl={buildUrl('assets/icons/toolbar_cg_save.svg')}
                  />
                  <Button
                    disabled={editableFilter}
                    onClick={this.handleOpenDelete}
                    title="Zmazať filter"
                    imageUrl={buildUrl('assets/icons/toolbar_cg_delete.svg')}
                  />
                  <Button
                    onClick={this.previous}
                    icon="arrow-left"
                    title="Predchádzajúci filter"
                    disabled={this.disabledPrevious()}
                  />
                  <Button
                    onClick={this.next}
                    icon="arrow-right"
                    title="Ďalší filter"
                    disabled={this.disabledNext()}
                  />
                </div>
                <Checkbox
                  name="Start"
                  label="Štartovací filter"
                  onChange={this.handleStartFilter}
                  value={this.state.startFilter}
                  disabled={startableFilter}
                />
                <div className="right">
                  <ComboBox
                    disabled={this.props.userFilterDisabled}
                    name={'user'}
                    data={this.props.managedUsers || []}
                    textField="NUz"
                    onChange={this.handleChangeUser}
                    value={this.props.managedUsers?.find(el => el.IUz === this.state.user)}
                    onBlur={this.onBlurUser}
                  />
                  <DropDownList
                    disabled={this.props.userFilterDisabled}
                    name={'selectFilter'}
                    data={this.props.userFilterList || []}
                    textField="name"
                    onChange={this.handleChangeUserFilter}
                    value={this.state.selectFilter || { name: ' - ', filters: [] }}
                    defaultItem={{ name: ' - ', filters: [] }}
                  />
                </div>
              </div>
            }
            {this.state.alertMsg &&
              <div className={"filter-alert mb-0 " + (this.state.error ? 'error' : 'success')}>
                <span className="alert-text">{this.state.alertMsg}</span>
              </div>
            }

            <div className="k-form-inline dialogFilterExtended">
              <div >
                <Button
                  buttonWrapperClassName="addButtonWrapper"
                  onClick={this.addNewFilter}
                  icon="add"
                />
              </div>
              {this.state.filter?.length > 0 && <table>
                <thead>
                  <tr>
                    <th>
                      logika
                    </th>
                    <th>
                      ľavá zátvorka
                    </th>
                    <th>
                      formulár
                    </th>
                    <th>
                      stĺpec
                    </th>
                    <th>
                      operátor
                    </th>
                    <th>
                      hodnota
                    </th>
                    <th>
                      pravá zátvorka
                    </th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {this.buildFilterRow()}
                </tbody>
              </table>}
            </div>
          </div>
        </Dialog>
        <SaveFilter
          isOpen={this.state.isOpenSaveDialog}
          handleClose={this.handleCloseSaveDialog}
          handleConfirm={this.handleSaveFilter}
          error={this.state.nameError}
        />
        <ConfirmDialog
          isOpen={this.state.isOpenDelete}
          onClose={this.handleCloseDelete}
          onConfirm={this.handleConfirmDelete}
          text={`Zmazať filter "${this.state.selectFilter?.name}"?`}
        />
      </>
    );
  }
}

DialogFilterExtended.propTypes = {
  schema: PropTypes.array.isRequired,
  parentProps: PropTypes.object,
  isOpen: PropTypes.bool.isRequired,
  handleOpenFilter: PropTypes.func.isRequired,
  handleCloseFilter: PropTypes.func.isRequired,
  handleConfirmFilter: PropTypes.func.isRequired,
  filter: PropTypes.object.isRequired,
  enumDefault: PropTypes.string.isRequired,
  primaryField: PropTypes.string.isRequired,
  userFilter: PropTypes.bool.isRequired,
  userFilterList: PropTypes.array,
  userFilterDisabled: PropTypes.bool.isRequired,
  updateFilter: PropTypes.func,
  getSchema: PropTypes.func,
  db: PropTypes.string
}

export default DialogFilterExtended;